/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { SortablePanelItem } from './SortablePanels';
import { PANEL_COLLAPSED_SIZE } from '../ResizablePanel/ResizablePanel';

export const SORTABLE_PANEL_GAP = 4;

interface Allocation {
  id: string;
  minimum: number;
  maximum?: number;
  weight?: number;
}

/** Equal shares, or proportional growth, constrained by each panel's limits. */
export const allocatePanelSpace = (
  panels: Allocation[],
  available: number,
): Record<string, number> => {
  // Find the common scale whose clamped shares fill the available space.
  // If minima exceed the viewport they deliberately produce overflow.
  let low = 0;
  let high = Math.max(0, available);
  const sizeAt = (panel: Allocation, scale: number): number =>
    Math.max(
      panel.minimum,
      Math.min(panel.maximum ?? Infinity, scale * (panel.weight ?? 1)),
    );
  for (let iteration = 0; iteration < 60; iteration += 1) {
    const scale = (low + high) / 2;
    const used = panels.reduce((sum, panel) => sum + sizeAt(panel, scale), 0);
    if (used > available) {
      high = scale;
    } else {
      low = scale;
    }
  }
  return Object.fromEntries(
    panels.map((panel) => [panel.id, Math.floor(sizeAt(panel, low) + 1e-7)]),
  );
};

interface PanelSize {
  size: number;
  savedSize?: number;
}

export const usePanelSizes = (
  panels: SortablePanelItem[],
  direction: 'horizontal' | 'vertical',
  sizing: 'initial' | 'untilManualResize' | undefined,
  growSignal: number | undefined,
): {
  viewportRef: React.RefObject<HTMLDivElement | null>;
  rootRef: React.RefObject<HTMLDivElement | null>;
  sizes: Record<string, number>;
  onSizeChange: (id: string, size: number) => void;
  onResizeStart: () => void;
  onOpenChange: (id: string, open: boolean) => void;
} => {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const latestPanels = React.useRef(panels);
  const cached = React.useRef<Record<string, PanelSize>>({});
  const localOpen = React.useRef<Record<string, boolean>>({});
  const userResized = React.useRef(false);
  const previousExtent = React.useRef<number | undefined>(undefined);
  const previousGrowSignal = React.useRef(growSignal);
  const [sizes, setSizes] = React.useState<Record<string, number>>({});

  const measure = React.useCallback(
    (grow = false): void => {
      const viewport = viewportRef.current;
      const root = rootRef.current;
      if (!sizing || !viewport || !root) {
        return;
      }
      const vertical = direction === 'vertical';
      const extent = vertical ? viewport.clientHeight : viewport.clientWidth;
      const extentChanged = extent > 0 && previousExtent.current !== extent;
      if (extent > 0) {
        previousExtent.current = extent;
      }
      const visible = latestPanels.current.filter((panel) => panel.isDisplayed);
      const rootStyle = window.getComputedStyle(root);
      const spacing =
        (parseFloat(vertical ? rootStyle.paddingTop : rootStyle.paddingLeft) ||
          0) +
        (parseFloat(
          vertical ? rootStyle.paddingBottom : rootStyle.paddingRight,
        ) || 0) +
        Math.max(0, visible.length - 1) * SORTABLE_PANEL_GAP;
      const minimum = (panel: SortablePanelItem): number =>
        Math.max(PANEL_COLLAPSED_SIZE, panel.minWidth ?? PANEL_COLLAPSED_SIZE);
      const open = (panel: SortablePanelItem): boolean =>
        localOpen.current[panel.id] ?? panel.isOpen !== false;
      const next = { ...cached.current };
      latestPanels.current.forEach((panel) => {
        if (next[panel.id]?.savedSize !== panel.customSize) {
          delete next[panel.id];
        }
        if (panel.customSize !== undefined && !next[panel.id]) {
          next[panel.id] = {
            size: Math.max(
              minimum(panel),
              Math.min(panel.maxWidth ?? Infinity, panel.customSize),
            ),
            savedSize: panel.customSize,
          };
        }
      });
      const pending = visible.filter(
        (panel) =>
          extent > 0 &&
          open(panel) &&
          panel.customSize === undefined &&
          (!next[panel.id] ||
            (extentChanged &&
              sizing === 'untilManualResize' &&
              !userResized.current)),
      );
      const pendingIds = new Set(pending.map((panel) => panel.id));
      const used = visible.reduce((sum, panel) => {
        if (!open(panel)) {
          return sum + PANEL_COLLAPSED_SIZE;
        }
        return (
          sum +
          (pendingIds.has(panel.id)
            ? 0
            : (next[panel.id]?.size ?? minimum(panel)))
        );
      }, 0);
      const allocated = allocatePanelSpace(
        pending.map((panel) => ({
          id: panel.id,
          minimum: minimum(panel),
          maximum: panel.maxWidth,
        })),
        extent - spacing - used,
      );
      pending.forEach((panel) => {
        next[panel.id] = { size: allocated[panel.id] };
      });

      if (grow && extent > 0) {
        const closedSize =
          visible.filter((panel) => !open(panel)).length * PANEL_COLLAPSED_SIZE;
        const grown = allocatePanelSpace(
          visible.filter(open).map((panel) => ({
            id: panel.id,
            minimum: next[panel.id]?.size ?? minimum(panel),
            maximum: panel.maxWidth,
            weight: next[panel.id]?.size ?? minimum(panel),
          })),
          extent - spacing - closedSize,
        );
        Object.entries(grown).forEach(([id, size]) => {
          next[id] = { ...next[id], size };
        });
      }
      const ids = new Set(latestPanels.current.map((panel) => panel.id));
      Object.keys(next).forEach((id) => {
        if (!ids.has(id)) {
          delete next[id];
          delete localOpen.current[id];
        }
      });
      cached.current = next;
      const nextSizes = Object.fromEntries(
        Object.entries(next).map(([id, value]) => [id, value.size]),
      );
      setSizes((previous) =>
        Object.keys(previous).length === Object.keys(nextSizes).length &&
        Object.entries(nextSizes).every(([id, size]) => previous[id] === size)
          ? previous
          : nextSizes,
      );
    },
    [direction, sizing],
  );

  React.useLayoutEffect(() => {
    panels.forEach((panel) => {
      const previous = latestPanels.current.find(({ id }) => id === panel.id);
      if (previous?.isOpen !== panel.isOpen) {
        delete localOpen.current[panel.id];
      }
    });
    latestPanels.current = panels;
    const grow = previousGrowSignal.current !== growSignal;
    previousGrowSignal.current = growSignal;
    measure(grow);
  }, [panels, growSignal, measure]);

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!sizing || !viewport || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(viewport);
    return (): void => observer.disconnect();
  }, [sizing, measure]);

  const onSizeChange = React.useCallback((id: string, size: number): void => {
    userResized.current = true;
    cached.current[id] = {
      size,
      savedSize: latestPanels.current.find((panel) => panel.id === id)
        ?.customSize,
    };
    setSizes((previous) => ({ ...previous, [id]: size }));
  }, []);
  const onResizeStart = React.useCallback((): void => {
    userResized.current = true;
  }, []);
  const onOpenChange = React.useCallback(
    (id: string, open: boolean): void => {
      localOpen.current[id] = open;
      measure();
    },
    [measure],
  );

  return {
    viewportRef,
    rootRef,
    sizes,
    onSizeChange,
    onOpenChange,
    onResizeStart,
  };
};
